/** Unsubmitted teacher text only. Tab-local, account-scoped, expiring, never sent to a frame. */
const PREFIX = 'tip:teacher-draft:v1:';
const TTL = 24 * 60 * 60 * 1000;
const MAX_TEXT = 10_000;
const MAX_DRAFTS = 32;

function storage(): Storage | null {
  try { return window.sessionStorage; } catch { return null; }
}
function key(scope: string, slot: string): string { return PREFIX + JSON.stringify([scope, slot]); }

export function readTeacherDraft(scope: string | undefined, slot: string): string {
  if (!scope) return '';
  try {
    const s = storage();
    const raw = s?.getItem(key(scope, slot));
    if (!raw || raw.length > MAX_TEXT * 6 + 200) return '';
    const saved: unknown = JSON.parse(raw);
    if (typeof saved !== 'object' || saved === null) return '';
    const d = saved as Record<string, unknown>;
    if (typeof d.text !== 'string' || d.text.length > MAX_TEXT || typeof d.at !== 'number' ||
        !Number.isFinite(d.at) || d.at > Date.now() || Date.now() - d.at > TTL) {
      s?.removeItem(key(scope, slot)); return '';
    }
    return d.text;
  } catch { return ''; }
}

/** false means the browser refused storage; callers must not claim the text was saved. */
export function writeTeacherDraft(scope: string | undefined, slot: string, text: string): boolean {
  if (!scope || text.length > MAX_TEXT) return false;
  try {
    const s = storage(); if (!s) return false;
    if (text === '') { s.removeItem(key(scope, slot)); return true; }
    const records: { key: string; at: number }[] = [];
    for (let i = 0; i < s.length; i++) {
      const k = s.key(i); if (!k?.startsWith(PREFIX)) continue;
      let at = 0;
      try { at = JSON.parse(s.getItem(k) ?? '{}').at; } catch { /* invalid entry expires */ }
      records.push({ key: k, at: typeof at === 'number' && Number.isFinite(at) ? at : 0 });
    }
    for (const r of records) if (Date.now() - r.at > TTL || r.at > Date.now()) s.removeItem(r.key);
    const fresh = records.filter(r => s.getItem(r.key) !== null && r.key !== key(scope, slot)).sort((a,b) => a.at - b.at);
    while (fresh.length >= MAX_DRAFTS) s.removeItem(fresh.shift()!.key);
    s.setItem(key(scope, slot), JSON.stringify({ text, at: Date.now() }));
    return true;
  } catch { return false; }
}

export function clearTeacherDrafts(): void {
  try {
    const s = storage(); if (!s) return;
    for (let i = s.length - 1; i >= 0; i--) {
      const k = s.key(i); if (k?.startsWith(PREFIX)) s.removeItem(k);
    }
  } catch { /* Storage disabled: do not prevent sign-out. */ }
}
